/**
 * Cutting a terrain polygon along the ground mesh's own cells, so that a draped
 * plate is *coplanar with the surface under it* rather than merely touching it
 * at its corners.
 *
 * ## Why this exists — the user-visible bug it removes
 *
 * `relief-ground.ts` draws the ground as a lattice of quads, each split into
 * two triangles. A landcover plate laid on top of it is a different mesh with a
 * different tessellation, and **two differently-tessellated approximations of
 * one surface always cross**. Sampling the drawn surface (rather than the
 * analytic field) makes the plate's *vertices* land exactly on the ground, which
 * is necessary and is not sufficient: between two vertices the plate is a chord,
 * and wherever that chord spans a crease in the mesh the bare ground comes up
 * **through** the field. Measured over Verdun's real relief with the shipped
 * 100 m subdivision, that is up to **0.44 m near the spawn and 1.0 m twenty
 * kilometres out**, with a mean absolute separation of 29 mm — i.e. two surfaces
 * interleaving over the whole map, which is exactly what a pilot reports as
 * z-fighting.
 *
 * A depth bias cannot fix that: it is not a depth-precision tie, it is real
 * geometry crossing real geometry. Subdividing harder only halves it (the error
 * is linear in the plate's edge length, because it is *slope discontinuity ×
 * edge*, not the quadratic sag of a smooth surface) and costs four times the
 * triangles per halving.
 *
 * ## What it does instead — remove the tie rather than bias it
 *
 * Cut the plate on the mesh's own cell boundaries **and on each cell's
 * diagonal**. Every output triangle then lies inside exactly one mesh triangle,
 * where the surface is planar, so "draped onto the ground" is true everywhere
 * and not just at the corners. The measured poke-through goes from 0.44 m to
 * **3e-14 m** — floating-point noise — and the constant `GROUND_DATUM_OFFSET_M`
 * clearance the flat world always had is then exact again.
 *
 * It is also *cheaper* than the fixed subdivision it replaces, because the
 * output is one triangle per (polygon, mesh-triangle) overlap instead of one per
 * fixed-size cell of a polygon: a 6 km test plate goes from 12 288 triangles to
 * 3 441 at the spawn and to 589 twenty kilometres out, where the mesh's lattice
 * is coarse and the old fixed edge was subdividing far past any visible need.
 *
 * This follows the same rule the 2026-08-23 z-fighting lane settled on for
 * coincident edge lines: when a depth tie has a structural cause, delete the
 * cause. No `polygonOffset`, no `renderOrder`, no depth flags.
 */

/**
 * The ground mesh's lattice, as much of it as a clipper needs.
 *
 * Both axes share one axis table because `relief-ground.ts` grades x and z
 * identically, which is what lets a single `axisAt` serve for both.
 */
export interface GroundLattice {
  /** Cells per axis. Lattice lines are `0 … steps`. */
  readonly steps: number;
  /** World coordinate of lattice line `i`. */
  readonly axisAt: (i: number) => number;
  /** Index of the cell containing `v`, clamped into `[0, steps - 1]`. */
  readonly cellIndexAt: (v: number) => number;
}

/** A polygon as a flat run of `x, y, z` triples. */
type Poly = number[];

/**
 * Ceiling on the triangles one source polygon may produce, so a mis-scaled or
 * degenerate input cannot hang a region load. A plate that hits this keeps the
 * cells it managed — the failure mode is coarse ground under a field, which is
 * what shipped before this module, rather than a stalled load.
 */
export const MAX_CLIP_TRIANGLES_PER_FACE = 200_000;

/**
 * Area below which an output triangle is dropped, m². Clipping a polygon
 * against four cell walls and a diagonal routinely produces slivers where the
 * polygon merely grazes a cell; they contribute no pixels and cost a vertex
 * each.
 */
const MIN_CLIP_AREA_M2 = 1e-6;

/**
 * Sutherland-Hodgman against one half-plane, interpolating all three
 * coordinates. `signedDistance` is positive inside.
 */
function clipHalfPlane(poly: Poly, signedDistance: (x: number, z: number) => number): Poly {
  const out: Poly = [];
  const count = poly.length / 3;
  for (let i = 0; i < count; i++) {
    const a = i * 3;
    const b = ((i + 1) % count) * 3;
    const da = signedDistance(poly[a]!, poly[a + 2]!);
    const db = signedDistance(poly[b]!, poly[b + 2]!);
    if (da >= 0) out.push(poly[a]!, poly[a + 1]!, poly[a + 2]!);
    if ((da > 0 && db < 0) || (da < 0 && db > 0)) {
      const t = da / (da - db);
      out.push(
        poly[a]! + (poly[b]! - poly[a]!) * t,
        poly[a + 1]! + (poly[b + 1]! - poly[a + 1]!) * t,
        poly[a + 2]! + (poly[b + 2]! - poly[a + 2]!) * t,
      );
    }
  }
  return out;
}

/** Fans a convex clipped polygon into triangles, dropping slivers. */
function fanInto(poly: Poly, emit: (tri: Float64Array) => void): number {
  const count = poly.length / 3;
  const tri = new Float64Array(9);
  let emitted = 0;
  for (let k = 1; k + 1 < count; k++) {
    for (let c = 0; c < 3; c++) {
      tri[c] = poly[c]!;
      tri[3 + c] = poly[k * 3 + c]!;
      tri[6 + c] = poly[(k + 1) * 3 + c]!;
    }
    const area =
      Math.abs((tri[3]! - tri[0]!) * (tri[8]! - tri[2]!) - (tri[6]! - tri[0]!) * (tri[5]! - tri[2]!)) / 2;
    if (area <= MIN_CLIP_AREA_M2) continue;
    emit(tri);
    emitted++;
  }
  return emitted;
}

/**
 * Splits one world-space triangle into pieces that each lie inside a single
 * mesh triangle, calling `emit` for every piece. Winding is preserved, so a
 * caller's face normals and back-face culling are unchanged.
 *
 * The lattice's outermost cells are treated as **unbounded** outward: a plate
 * can reach past the mesh's 80 km rim (the shipped regions are up to 107 km
 * across and the mesh is centred on one aerodrome), and the surface sampler
 * clamps out there, so cutting the polygon at the rim would delete field the
 * region ships rather than lay it down.
 */
export function clipTriangleToLattice(
  triangle: ArrayLike<number>,
  lattice: GroundLattice,
  emit: (tri: Float64Array) => void,
): number {
  const xsOf = (i: number): number => lattice.axisAt(i);
  let minX = Infinity, maxX = -Infinity, minZ = Infinity, maxZ = -Infinity;
  for (let c = 0; c < 3; c++) {
    const x = triangle[c * 3]!;
    const z = triangle[c * 3 + 2]!;
    if (x < minX) minX = x;
    if (x > maxX) maxX = x;
    if (z < minZ) minZ = z;
    if (z > maxZ) maxZ = z;
  }
  const ix0 = lattice.cellIndexAt(minX);
  const ix1 = lattice.cellIndexAt(maxX);
  const iz0 = lattice.cellIndexAt(minZ);
  const iz1 = lattice.cellIndexAt(maxZ);
  const last = lattice.steps - 1;

  let emitted = 0;
  for (let iz = iz0; iz <= iz1; iz++) {
    const z0 = xsOf(iz);
    const z1 = xsOf(iz + 1);
    const zLo = iz === 0 ? -Infinity : z0;
    const zHi = iz === last ? Infinity : z1;
    for (let ix = ix0; ix <= ix1 && emitted < MAX_CLIP_TRIANGLES_PER_FACE; ix++) {
      const x0 = xsOf(ix);
      const x1 = xsOf(ix + 1);
      const xLo = ix === 0 ? -Infinity : x0;
      const xHi = ix === last ? Infinity : x1;

      let poly: Poly = [
        triangle[0]!, triangle[1]!, triangle[2]!,
        triangle[3]!, triangle[4]!, triangle[5]!,
        triangle[6]!, triangle[7]!, triangle[8]!,
      ];
      if (xLo > -Infinity) poly = clipHalfPlane(poly, (x) => x - xLo);
      if (poly.length >= 9 && xHi < Infinity) poly = clipHalfPlane(poly, (x) => xHi - x);
      if (poly.length >= 9 && zLo > -Infinity) poly = clipHalfPlane(poly, (_x, z) => z - zLo);
      if (poly.length >= 9 && zHi < Infinity) poly = clipHalfPlane(poly, (_x, z) => zHi - z);
      if (poly.length < 9) continue;

      // `relief-ground.ts` emits (x0,z0)-(x0,z1)-(x1,z0) and
      // (x1,z0)-(x0,z1)-(x1,z1), so the shared diagonal is u + v = 1.
      const dx = x1 - x0;
      const dz = z1 - z0;
      const diagonal = (x: number, z: number): number => 1 - ((x - x0) / dx + (z - z0) / dz);
      emitted += fanInto(clipHalfPlane(poly, diagonal), emit);
      emitted += fanInto(clipHalfPlane(poly, (x, z) => -diagonal(x, z)), emit);
    }
  }
  return emitted;
}

/**
 * Ceiling on the pieces one hairline segment may become. An 11 km segment — the
 * longest Verdun ships — crosses about forty cells, so this is two orders of
 * margin and exists only so a NaN endpoint cannot spin.
 */
const MAX_CLIP_PIECES_PER_SEGMENT = 4096;

/**
 * Cuts one world-space line segment at every mesh cell wall and cell diagonal it
 * crosses, calling `emit` with `[ax, ay, az, bx, by, bz]` per piece.
 *
 * The road and river network needs this for the same reason a plate does, and
 * more sharply. Draping moves endpoints, and Verdun's triangle-outline segments
 * run to **11.4 km** — 815 of its 3 510 are over a kilometre. Draped by their
 * two ends alone, such a line is a straight chord over ground that rolls ±42 m
 * beneath it, so a river disappears into every hill it crosses and floats over
 * every valley, and where it grazes the surface it interleaves with it — which
 * is the shallow-angle stipple a pilot reads as z-fighting. Cut on the cells,
 * every piece spans planar ground and the constant datum clearance holds along
 * the whole length.
 */
export function clipSegmentToLattice(
  segment: ArrayLike<number>,
  lattice: GroundLattice,
  emit: (piece: Float64Array) => void,
): number {
  const ax = segment[0]!, ay = segment[1]!, az = segment[2]!;
  const bx = segment[3]!, by = segment[4]!, bz = segment[5]!;
  const cuts: number[] = [];

  const gatherAxis = (from: number, to: number): void => {
    const span = to - from;
    if (span === 0) return;
    const lo = lattice.cellIndexAt(Math.min(from, to));
    const hi = lattice.cellIndexAt(Math.max(from, to));
    for (let i = lo; i <= hi + 1 && i <= lattice.steps; i++) {
      const t = (lattice.axisAt(i) - from) / span;
      if (t > 0 && t < 1) cuts.push(t);
    }
  };
  gatherAxis(ax, bx);
  gatherAxis(az, bz);

  cuts.sort((p, q) => p - q);
  // The cell diagonal is a crease too. Each piece now lies in one cell, so its
  // crossing is a single linear solve against that cell's `u + v = 1`.
  const withDiagonals: number[] = [];
  let previous = 0;
  for (const t of [...cuts, 1]) {
    if (t - previous > 1e-12) {
      const midT = (previous + t) / 2;
      const ix = lattice.cellIndexAt(ax + (bx - ax) * midT);
      const iz = lattice.cellIndexAt(az + (bz - az) * midT);
      const x0 = lattice.axisAt(ix), z0 = lattice.axisAt(iz);
      const dx = lattice.axisAt(ix + 1) - x0, dz = lattice.axisAt(iz + 1) - z0;
      const at = (t2: number): number =>
        1 - ((ax + (bx - ax) * t2 - x0) / dx + (az + (bz - az) * t2 - z0) / dz);
      const da = at(previous), db = at(t);
      if ((da > 0 && db < 0) || (da < 0 && db > 0)) withDiagonals.push(previous + (t - previous) * (da / (da - db)));
    }
    withDiagonals.push(t);
    previous = t;
  }

  const piece = new Float64Array(6);
  let emitted = 0;
  let from = 0;
  for (const t of withDiagonals) {
    if (t - from <= 1e-12) continue;
    if (emitted >= MAX_CLIP_PIECES_PER_SEGMENT) break;
    piece[0] = ax + (bx - ax) * from;
    piece[1] = ay + (by - ay) * from;
    piece[2] = az + (bz - az) * from;
    piece[3] = ax + (bx - ax) * t;
    piece[4] = ay + (by - ay) * t;
    piece[5] = az + (bz - az) * t;
    emit(piece);
    emitted++;
    from = t;
  }
  if (emitted === 0) {
    piece.set([ax, ay, az, bx, by, bz]);
    emit(piece);
    emitted = 1;
  }
  return emitted;
}
